/**
 * useEvidence Hook
 *
 * React hook for fetching and managing evidence data for the Click-to-Explain modal.
 * Supports both live API and mock data for development.
 * Includes month selection with auto-default to most recent month with data.
 *
 * BUG-UI-092: Added memberId support for cross-run historical evidence.
 * When memberId is provided, fetches member history to populate month selector.
 *
 * @see auditEvidenceService.ts
 * @see mockEvidenceData.ts
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { EvidenceContract, DisclosureLevel, redactForLevel } from '@/types/evidenceContract';
import {
  getRunEvidence,
  getRunTransactions,
  transformToEvidenceContract,
  EvidenceQueryRecord,
  EvidenceRunMetadata,
} from '@/services/auditEvidenceService';
import { getMockEvidence, shouldUseMockData } from '@/services/mockEvidenceData';
import { getEvidenceConfig } from '@/config/uiEvidenceMap';
import { getMemberHistory, MemberHistoryPoint } from '@/services/lenderApiService';
import { computeDemo2ShiftOffset, shiftMonthKey } from '@/utils/demo2EvidenceFallback';
import { isDemo2Member } from '@/services/demo2PersonaOverlay';

interface UseEvidenceOptions {
  runId: string;
  /** BUG-UI-092: Member ID for cross-run historical evidence */
  memberId?: string;
  uiFieldPath: string;
  disclosureLevel: DisclosureLevel;
  enabled?: boolean;
  /** Option 3: Initial month to display (from card's selected month) */
  initialMonth?: string | null;
}

export interface MonthOption {
  month_key: string;
  label: string;
  hasData: boolean;
  value: number;
  /** BUG-UI-092: Source of this month option - 'run' (from algo_run_evidence) or 'history' (from member history) */
  source?: 'run' | 'history';
}

interface UseEvidenceResult {
  evidence: EvidenceContract | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isMockData: boolean;
  // Month selection
  availableMonths: MonthOption[];
  selectedMonth: string | null;
  setSelectedMonth: (month: string) => void;
  // Epic 65M: Run-level metadata for Trust Panel
  runMetadata?: EvidenceRunMetadata;
}

/**
 * Helper to extract main value from intermediate_metrics for sorting
 * BUG-UI-045: Added total_* fields that backend emits
 * BUG-UI-076 FIX: Accept metricKey to prioritize ratio fields for ratio metrics
 */
function getMainValue(metrics: Record<string, unknown> | undefined, metricKey?: string): number {
  if (!metrics) return 0;

  // BUG-UI-076 FIX: For ratio metrics, prioritize ratio fields first
  if (metricKey && (metricKey.includes('dti') || metricKey.includes('ratio'))) {
    const ratioKeys = ['dti_ratio', 'ratio', 'debt_to_income_ratio'];
    for (const key of ratioKeys) {
      const val = metrics[key];
      if (typeof val === 'number' && !Number.isNaN(val)) return val;
    }
  }

  // BUG-UI-045: Include total_* fields that backend emits for debt/cash metrics
  const keys = [
    'sum_deposits', 'sum_withdrawals', 'money_in', 'money_out',
    'total', 'value', 'score',
    // BUG-UI-045: Add total_* fields
    'total_actual_debt_payments', 'total_cash_on_hand', 'total_debt',
    'minimum_payments_all_debt', 'net_savings', 'money_left_over'
  ];
  for (const key of keys) {
    const val = metrics[key];
    if (typeof val === 'number' && !Number.isNaN(val)) return val;
  }
  return 0;
}

/**
 * Format month_key (YYYY-MM) to readable label
 */
function formatMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * BUG-UI-092: Extract value from member history point based on metric key
 * Maps metric keys to the corresponding fields in MemberHistoryPoint
 */
function getHistoryValueForMetric(historyPoint: MemberHistoryPoint, metricKey: string): number {
  // VeraScore
  if (metricKey.includes('verascore')) {
    return historyPoint.score || 0;
  }
  // Factor scores
  if (metricKey.includes('cash_flow') || metricKey === 'factor_spending') {
    return historyPoint.factors?.cash_flow ?? 0;
  }
  if (metricKey.includes('debt_to_income') || metricKey === 'factor_dti') {
    return historyPoint.factors?.debt_to_income ?? 0;
  }
  if (metricKey.includes('liquid_reserves') || metricKey === 'factor_cash_on_hand') {
    return historyPoint.factors?.liquid_reserves ?? 0;
  }
  if (metricKey.includes('savings') || metricKey === 'factor_savings') {
    return historyPoint.factors?.savings ?? 0;
  }
  if (metricKey.includes('debt_management') || metricKey === 'factor_debt') {
    return historyPoint.factors?.debt ?? 0;
  }
  if (metricKey.includes('payment_history') || metricKey === 'factor_payment_history') {
    return historyPoint.factors?.payment_history ?? 0;
  }
  // Input metrics
  if (metricKey.includes('gross_income') || metricKey === 'money_in_1m') {
    return historyPoint.inputs?.gross_income ?? historyPoint.inputs?.yearly_gross_income ?? 0;
  }
  if (metricKey.includes('net_savings')) {
    return historyPoint.inputs?.net_savings ?? 0;
  }
  if (metricKey.includes('liquid_reserves') && !metricKey.includes('factor')) {
    return historyPoint.inputs?.liquid_reserves ?? 0;
  }
  if (metricKey.includes('dti_ratio')) {
    return historyPoint.inputs?.dti_ratio ?? 0;
  }
  // Default to VeraScore
  return historyPoint.score || 0;
}

/**
 * Hook for fetching evidence data with month selection
 * BUG-UI-092: Added memberId support for cross-run historical months
 * Option 3: Added initialMonth support to open modal at selected month
 */
export function useEvidence({
  runId,
  memberId,
  uiFieldPath,
  disclosureLevel,
  enabled = true,
  initialMonth,
}: UseEvidenceOptions): UseEvidenceResult {
  const [allRecords, setAllRecords] = useState<EvidenceQueryRecord[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMockData, setIsMockData] = useState(false);
  const [transactions, setTransactions] = useState<any>(undefined);
  // BUG-UI-003: Store mock evidence separately so it can be used when API returns no data
  const [mockEvidence, setMockEvidence] = useState<EvidenceContract | null>(null);
  // BUG-UI-092: Store member history for cross-run month selector
  const [memberHistory, setMemberHistory] = useState<MemberHistoryPoint[]>([]);
  // BUG-EPIC61-009: Store run-level metadata for Trust Panel
  const [runMetadata, setRunMetadata] = useState<EvidenceRunMetadata | undefined>(undefined);

  // BUG-UI-042: Prevent duplicate API calls using refs to track fetch state
  const fetchInProgressRef = useRef(false);
  const lastFetchKeyRef = useRef<string>('');
  const lastFetchTimeRef = useRef<number>(0);

  // Get metric key from UI field path
  const evidenceConfig = getEvidenceConfig(uiFieldPath);
  const metricKey = evidenceConfig?.metric_key || '';

  // Phase 14 (Demo2): when the member's history anchor (latest score_month) is
  // ahead of the run evidence's max month_key, shift run months forward by the
  // offset so dropdown labels align with the displayed Demo2 demo window. The
  // shift is display-only; evidence lookup translates back via -offset.
  const shiftOffset = useMemo(() => {
    if (!memberId || allRecords.length === 0 || memberHistory.length === 0) return 0;
    const runMax = allRecords
      .map(r => r.month_key)
      .filter((m): m is string => !!m)
      .sort()
      .pop() ?? null;
    const histMax = memberHistory
      .map(h => h.month)
      .filter((m): m is string => !!m)
      .sort()
      .pop() ?? null;
    return computeDemo2ShiftOffset(memberId, histMax, runMax);
  }, [memberId, allRecords, memberHistory]);

  // BUG-UI-092: Build available months from BOTH run evidence AND member history
  // This allows showing all historical months even if evidence is only available for current run
  const availableMonths: MonthOption[] = useMemo(() => {
    // Start with months from algo_run_evidence (current run)
    // Phase 14: shift run month_keys forward for Demo2 demo-window alignment.
    const runMonths = allRecords
      .filter(r => r.month_key)
      .map(r => {
        const shifted = shiftOffset > 0 ? shiftMonthKey(r.month_key!, shiftOffset) : r.month_key!;
        return {
          month_key: shifted,
          label: formatMonthLabel(shifted),
          hasData: getMainValue(r.intermediate_metrics, metricKey) > 0,
          value: getMainValue(r.intermediate_metrics, metricKey),
          source: 'run' as const,
        };
      });

    // BUG-UI-092: Add months from member history (if available and not already in runMonths)
    const runMonthKeys = new Set(runMonths.map(m => m.month_key));
    const historyMonths = memberHistory
      .filter(h => h.month && !runMonthKeys.has(h.month))
      .map(h => ({
        month_key: h.month,
        label: formatMonthLabel(h.month),
        hasData: true, // History always has data
        value: getHistoryValueForMetric(h, metricKey),
        source: 'history' as const,
      }));

    // Merge and sort (most recent first)
    return [...runMonths, ...historyMonths]
      .sort((a, b) => b.month_key.localeCompare(a.month_key));
  }, [allRecords, memberHistory, metricKey, shiftOffset]);

  // Get current evidence based on selected month
  // BUG-UI-003: Use mockEvidence when allRecords is empty (API returned no data)
  // Phase 14: selectedMonth lives in shifted (display) space; translate back
  // by -shiftOffset to find the matching original-space record.
  const evidence = useMemo(() => {
    // If we have mock evidence and no API records, use mock
    if (allRecords.length === 0) {
      return mockEvidence;
    }

    const lookupKey = selectedMonth && shiftOffset > 0
      ? shiftMonthKey(selectedMonth, -shiftOffset)
      : selectedMonth;
    const record = lookupKey
      ? allRecords.find(r => r.month_key === lookupKey)
      : allRecords[0];

    if (!record) return mockEvidence;

    const contract = transformToEvidenceContract(record, transactions, disclosureLevel);
    contract.run_id = runId;
    // TODO: Re-enable role-based redaction after privacy redesign (currently bypassed)
    return redactForLevel(contract, 'AUDITOR');
  }, [allRecords, selectedMonth, transactions, disclosureLevel, runId, mockEvidence, shiftOffset]);

  // Phase 14: when shiftOffset transitions (e.g., memberHistory arrives after
  // auto-select), translate selectedMonth forward so it stays in display space.
  const lastOffsetRef = useRef(0);
  useEffect(() => {
    const prev = lastOffsetRef.current;
    if (shiftOffset !== prev) {
      const delta = shiftOffset - prev;
      if (delta !== 0) {
        setSelectedMonth(current => (current ? shiftMonthKey(current, delta) : current));
      }
      lastOffsetRef.current = shiftOffset;
    }
  }, [shiftOffset]);

  const fetchEvidence = useCallback(async () => {
    if (!runId || !metricKey) {
      setError('Missing run_id or metric_key');
      return;
    }

    // BUG-UI-042: Prevent duplicate API calls with debounce
    const fetchKey = `${runId}-${metricKey}`;
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;

    // Skip if same key and either in-progress or within 500ms debounce window
    if (lastFetchKeyRef.current === fetchKey) {
      if (fetchInProgressRef.current) {
        console.log(`⏭️ [useEvidence] Skipping duplicate fetch (in progress) for ${metricKey}`);
        return;
      }
      if (timeSinceLastFetch < 500) {
        console.log(`⏭️ [useEvidence] Skipping duplicate fetch (debounce ${timeSinceLastFetch}ms) for ${metricKey}`);
        return;
      }
    }

    fetchInProgressRef.current = true;
    lastFetchKeyRef.current = fetchKey;
    lastFetchTimeRef.current = now;

    setIsLoading(true);
    setError(null);
    // CRITICAL: Reset state when fetching new metric to prevent stale data display
    // This fixes BUG-UI-014 where switching factors showed old data
    setAllRecords([]);
    setSelectedMonth(null);
    setTransactions(undefined);
    setMockEvidence(null);  // BUG-UI-003: Reset mock evidence too
    setMemberHistory([]);   // BUG-UI-092: Reset member history

    try {
      // Check if we should use mock data
      if (shouldUseMockData()) {
        console.log(`📦 [useEvidence] Using mock data for ${metricKey}`);
        const mockData = getMockEvidence(metricKey);
        if (mockData) {
          // BUG-UI-003: Store mock evidence so it can be displayed
          setMockEvidence(mockData);
          setIsMockData(true);
          setIsLoading(false);
          return;
        }
      }

      // BUG-UI-092: Fetch member history for cross-run month selector
      // Do this in parallel with evidence fetch for better performance.
      // Phase 14: for Demo2 members we await the history so the auto-select
      // below can compute shiftOffset locally and translate initialMonth from
      // shifted (display) space back to original space for record matching.
      let localHistory: MemberHistoryPoint[] = [];
      if (memberId) {
        console.log(`📡 [useEvidence] Fetching member history for ${memberId} (BUG-UI-092)`);
        const historyPromise = getMemberHistory(memberId, {
          months: 24,
          includeFactors: true,
          includeInputs: true,
        })
          .then(historyResponse => {
            if (historyResponse?.series) {
              console.log(`✅ [useEvidence] Got ${historyResponse.series.length} months of history`);
              setMemberHistory(historyResponse.series);
              localHistory = historyResponse.series;
            }
          })
          .catch(historyError => {
            // Non-blocking - history is enhancement, not critical
            console.warn(`[useEvidence] Could not fetch member history: ${historyError}`);
          });
        if (isDemo2Member(memberId)) {
          await historyPromise;
        }
      }

      // Fetch ALL months from API (no month_key filter)
      console.log(`📡 [useEvidence] Fetching all months for run=${runId}, metric=${metricKey}`);

      const evidenceResponse = await getRunEvidence(runId, {
        entity_name: metricKey,
      });

      // BUG-EPIC61-009: Store run-level metadata for Trust Panel
      // BUG-UI-103 FIX: Set run_metadata BEFORE early return so credit_score can show "Last updated"
      if (evidenceResponse.run_metadata) {
        console.log(`📊 [useEvidence] Received run_metadata:`, evidenceResponse.run_metadata);
        setRunMetadata(evidenceResponse.run_metadata);
      }

      if (!evidenceResponse.evidence || evidenceResponse.evidence.length === 0) {
        setError(`No evidence found for ${metricKey}`);
        // allRecords already reset above, so modal won't show stale data
        return;
      }

      // Store all records
      const records = evidenceResponse.evidence.filter(
        (e: EvidenceQueryRecord) => e.entity_name === metricKey
      );
      setAllRecords(records);

      // Auto-select month: use initialMonth if provided, otherwise most recent with data
      const sortedByDate = [...records]
        .filter(r => r.month_key)
        .sort((a, b) => b.month_key!.localeCompare(a.month_key!));

      // Phase 14: compute local shift offset for Demo2 so initialMonth (in
      // shifted display space, sourced from MemberCard's chart) can be
      // translated back to original space for record matching, and the
      // resulting selectedMonth can be set in shifted space (matching the
      // dropdown labels rendered by availableMonths).
      const runMaxLocal = sortedByDate[0]?.month_key ?? null;
      const histMaxLocal = localHistory
        .map(h => h.month)
        .filter((m): m is string => !!m)
        .sort()
        .pop() ?? null;
      const localShiftOffset = computeDemo2ShiftOffset(memberId, histMaxLocal, runMaxLocal);

      // Option 3: If initialMonth is provided and exists in records, use it
      let selectedMonthToUse: string | null = null;

      // DEBUG: Log all available month_keys and the initialMonth we're looking for
      console.log(`📅 [useEvidence] Month matching debug:`, {
        initialMonth,
        initialMonthLength: initialMonth?.length,
        availableMonthKeys: sortedByDate.map(r => r.month_key),
        firstRecordMonthKey: sortedByDate[0]?.month_key,
        firstRecordMonthKeyLength: sortedByDate[0]?.month_key?.length,
        localShiftOffset,
      });

      if (initialMonth) {
        // Normalize initialMonth to YYYY-MM format if it's longer (e.g., "2025-08-01" -> "2025-08")
        const normalizedInitialMonth = initialMonth.length > 7 ? initialMonth.slice(0, 7) : initialMonth;
        // Phase 14: initialMonth is in shifted (display) space when Demo2;
        // translate back to original space for matching against records.
        const originalLookup = localShiftOffset > 0
          ? shiftMonthKey(normalizedInitialMonth, -localShiftOffset)
          : normalizedInitialMonth;
        console.log(`📅 [useEvidence] Normalized initialMonth: "${initialMonth}" -> "${normalizedInitialMonth}" (lookup="${originalLookup}")`);

        const initialMonthRecord = sortedByDate.find(r => r.month_key === originalLookup);
        if (initialMonthRecord) {
          selectedMonthToUse = localShiftOffset > 0
            ? shiftMonthKey(originalLookup, localShiftOffset)
            : originalLookup;
          console.log(`📅 [useEvidence] Using initialMonth from card: ${selectedMonthToUse}`);
        } else {
          console.log(`📅 [useEvidence] initialMonth ${originalLookup} not found in records, falling back to auto-select`);
        }
      }

      // Fallback: BUG-UI-076 FIX: Pass metricKey to getMainValue for ratio metrics
      if (!selectedMonthToUse) {
        const monthWithData = sortedByDate.find(r => getMainValue(r.intermediate_metrics, metricKey) > 0);
        const originalAuto = monthWithData?.month_key || sortedByDate[0]?.month_key || null;
        // Phase 14: surface the auto-selected month in shifted space so it
        // aligns with the dropdown labels.
        selectedMonthToUse = originalAuto && localShiftOffset > 0
          ? shiftMonthKey(originalAuto, localShiftOffset)
          : originalAuto;
        console.log(`📅 [useEvidence] Auto-selected month: ${selectedMonthToUse} (has data: ${!!monthWithData})`);
      }

      // Phase 14: pre-seed lastOffsetRef so the offset-translation effect does
      // not double-shift selectedMonth on the first render after this fetch.
      lastOffsetRef.current = localShiftOffset;
      setSelectedMonth(selectedMonthToUse);

      // For AUDITOR level, also fetch transactions
      if (disclosureLevel === 'AUDITOR') {
        try {
          const txns = await getRunTransactions(runId, metricKey);
          setTransactions(txns);
        } catch (txnError) {
          console.warn(`[useEvidence] Could not fetch transactions: ${txnError}`);
        }
      }

      setIsMockData(false);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch evidence';
      console.error(`[useEvidence] Error: ${errorMessage}`);
      setError(errorMessage);

      // Try mock data as fallback
      const mockData = getMockEvidence(metricKey);
      if (mockData) {
        console.log(`📦 [useEvidence] Falling back to mock data for ${metricKey}`);
        setAllRecords([]);
        setIsMockData(true);
        setError(null);
      }
    } finally {
      setIsLoading(false);
      // BUG-UI-042: Reset fetch-in-progress flag when done
      fetchInProgressRef.current = false;
    }
  }, [runId, memberId, metricKey, disclosureLevel]);

  // Auto-fetch when enabled
  useEffect(() => {
    if (enabled) {
      fetchEvidence();
    }
  }, [enabled, fetchEvidence]);

  return {
    evidence,
    isLoading,
    error,
    refetch: fetchEvidence,
    isMockData,
    availableMonths,
    selectedMonth,
    setSelectedMonth,
    runMetadata, // BUG-EPIC61-009: Return run-level metadata for Trust Panel
  };
}

export default useEvidence;

